import InteractiveAnnotation from "./interactiveAnnotation";
import Ellipse from "./ellipse";
import { drawBoxes, measureText, setFontStyle, setLineStyle } from "../canvasUtils";
import MeasurementText from "./measurementText";

export default class CupToDisc extends InteractiveAnnotation {
  constructor(position, options) {
    super(position, options);

    this.innerEllipse = new Ellipse(position, options);
    this.outerEllipse = new Ellipse(position, options, 300);

    this.measurementTexts = {
      horCupDisc: new MeasurementText(),
      verCupDisc: new MeasurementText(),
      cupArea: new MeasurementText(),
      discArea: new MeasurementText()
    } 
    this.activeText = false;
  }

  get isActive() {
    return this.innerEllipse.isActive || this.outerEllipse.isActive || !!this.activeText;
  }

  get horCupDiscText() {
    let fraction = this.innerEllipse.radiusX / this.outerEllipse.radiusX;
    return fraction.toFixed(2) + " Hor. Cup/Disc";
  }

  get verCupDiscText() {
    let fraction = this.innerEllipse.radiusY / this.outerEllipse.radiusY;
    return fraction.toFixed(2) + " Ver. Cup/Disc";
  }

  get cupDiscTextPosition() {
    let x = this.outerEllipse.centerX + this.outerEllipse.radiusX + 15;
    let y = this.outerEllipse.centerY - 25;
    return { x, y };
  }

  get horTextWidth(){
    return measureText(this.horCupDiscText, this.font);
  }

  get verTextWidth(){
    return measureText(this.verCupDiscText, this.font);
  }

  get cupDiscTextWidth() {
    return Math.max(this.horTextWidth, this.verTextWidth);
  }

  get cupAreaText() {
    let area = this.innerEllipse.area * this.scale * this.scale;
    return "Cup Area: " + area.toFixed(2) + " mm²";
  }

  get discAreaText() {
    let area = this.outerEllipse.area * this.scale * this.scale;
    return "Disc Area: " + area.toFixed(2) + " mm²";
  }

  get cupTextWidth(){
    return  measureText(this.cupAreaText, this.font);
  }

  get discTextWidth(){
    return measureText(this.discAreaText, this.font);
  }

  get areaTextWidth() {
    return Math.max(this.cupTextWidth, this.discTextWidth);
  } 

  get areaTextPosition() {
    let x = this.outerEllipse.centerX - this.areaTextWidth / 2;
    let y = this.outerEllipse.centerY + this.outerEllipse.radiusY + 15;
    return { x, y };
  }

  get isTextActive() {
    return !!this.activeText;
  }

  draw(ctx) {
    setLineStyle(ctx, this);
    this.innerEllipse.draw(ctx);
    this.outerEllipse.draw(ctx);
    this.drawText(ctx);
    if(this.isTextActive) this.drawTextInteractionBoxes(ctx)   
    if (this.innerEllipse.isActive) drawBoxes(ctx, this.innerEllipse.getInteractionPoints(), this.innerEllipse.transform.zoom);
    if (this.outerEllipse.isActive) drawBoxes(ctx, this.outerEllipse.getInteractionPoints(), this.outerEllipse.transform.zoom);
  }

  drawText(ctx){
    setFontStyle(ctx, this);

    let position = this.cupDiscTextPosition; 
    this.measurementTexts.horCupDisc.updateAndDraw(this.horTextWidth, this.textSize, ctx, this.horCupDiscText, position.x, position.y);
    this.measurementTexts.verCupDisc.updateAndDraw(this.verTextWidth, this.textSize, ctx, this.verCupDiscText, position.x, position.y + this.textSize);

    position = this.areaTextPosition; 
    this.measurementTexts.cupArea.updateAndDraw(this.cupTextWidth, this.textSize, ctx, this.cupAreaText, position.x, position.y);
    this.measurementTexts.discArea.updateAndDraw(this.discTextWidth, this.textSize, ctx, this.discAreaText, position.x, position.y + this.textSize);
  }

  drawTextInteractionBoxes(ctx){  
    this.measurementTexts[this.activeText].drawTextInteractionBoxe(ctx);
  }

  updateTextLocation(x, y){
    this.measurementTexts[this.activeText].updateLocation(x, y)
  }

  isInBounds(x, y) {
    return this.innerEllipse.isInBounds(x, y) 
      || this.outerEllipse.isInBounds(x, y)
      ||  this.isInBoundsAllTexts(x, y);
  }

  isInBoundsAllTexts(x, y) {
    return this.measurementTexts.horCupDisc.isInBound(x, y)
      || this.measurementTexts.verCupDisc.isInBound(x, y)
      || this.measurementTexts.cupArea.isInBound(x, y)
      || this.measurementTexts.discArea.isInBound(x, y)
  }

  getHoverClass(x, y) {
    return this.innerEllipse.getHoverClass(x, y) || this.outerEllipse.getHoverClass(x, y);
  }

  mousedown(x, y) {
    this.deactivate();

    if (this.innerEllipse.isInBounds(x, y))
      this.innerEllipse.mousedown(x, y);
    else if (this.outerEllipse.getHoveredBox(x, y))
      this.outerEllipse.mousedown(x, y);
    else if(this.measurementTexts.horCupDisc.isInBound(x, y))
      this.activeText = "horCupDisc";
    else if(this.measurementTexts.verCupDisc.isInBound(x, y))
      this.activeText = "verCupDisc";
    else if(this.measurementTexts.cupArea.isInBound(x, y))
      this.activeText = "cupArea";
    else if(this.measurementTexts.discArea.isInBound(x, y))
      this.activeText = "discArea";
    else {
      this.innerEllipse.mousedown(x, y);
      this.outerEllipse.mousedown(x, y);
    }

    this.signalReady();
  }

  mousemove(x, y) {
    let oldInner = this.getEllipseState(this.innerEllipse);
    let oldOuter = this.getEllipseState(this.outerEllipse);

    if (this.activeText) this.updateTextLocation(x, y);
    else{
      if (this.innerEllipse.isActive) this.innerEllipse.mousemove(x, y);
      if (this.outerEllipse.isActive) this.outerEllipse.mousemove(x, y);
    }

    if (this.ellipseIntersection()) {
      this.restoreEllipseState(this.innerEllipse, oldInner);
      this.restoreEllipseState(this.outerEllipse, oldOuter);
    }

    this.signalReady();
  }

  getEllipseState(ellipse) {
    return {
      centerX: ellipse.centerX,
      centerY: ellipse.centerY,
      radiusX: ellipse.radiusX,
      radiusY: ellipse.radiusY
    };
  }

  ellipseIntersection() {
    let angle = -Math.PI;
    let step = Math.PI / 64;
    while (angle <= Math.PI) {
      let pos = this.innerEllipse.getArcPosition(angle);
      if (!this.outerEllipse.isWithinEllipse(pos.x, pos.y)) return true;
      angle += step;
    }
    return false;
  }

  restoreEllipseState(ellipse, oldState) {
    ellipse.centerX = oldState.centerX;
    ellipse.centerY = oldState.centerY;
    ellipse.radiusX = oldState.radiusX;
    ellipse.radiusY = oldState.radiusY;
  }

  deactivate() {
    this.innerEllipse.deactivate();
    this.outerEllipse.deactivate();
    this.activeText = null;
    this.signalReady();
  }
  
  setParentTransform(obj) {
    this.innerEllipse.setParentTransform(obj);
    this.outerEllipse.setParentTransform(obj);
    this.signalReady();
  }

  setLineWidth(width) {
    this.innerEllipse.setLineWidth(width);
    this.outerEllipse.setLineWidth(width);
    this.signalReady();
  }

  updateColor(color) 
  {
    this.innerEllipse.color = color;
    this.outerEllipse.color = color;
    this.color = color;
  }
}